import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getContentById } from '../data/content';
import { getCategoryPhotoObject } from '../lib/unsplash';
import UnsplashImage from '../components/UnsplashImage';
import Icon from '../components/shared/Icon';
import Comments from '../components/Comments';
import ClapButton from '../components/shared/ClapButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPalette } from '@fortawesome/free-solid-svg-icons';
import { PageHeader } from '../design-system';

export default function ActivityDetail() {
  const { activityId } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFirestoreContent, setIsFirestoreContent] = useState(false);
  const [photoObject, setPhotoObject] = useState(null);

  // Try to load from Firestore first (approved user submissions)
  useEffect(() => {
    if (!activityId) {
      setLoading(false);
      return;
    }

    const contentRef = doc(db, 'content', activityId);
    const unsubscribe = onSnapshot(
      contentRef,
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          if (data.type === 'activity') {
            // Convert Firestore content to activity format
            setActivity({
              id: doc.id,
              title: data.title,
              description: data.description,
              category: data.content.category || 'Activity',
              ageRange: data.content.ageRange || 'All ages',
              time: data.content.duration || 'N/A',
              image: data.content.imageUrl ? null : '🎨',
              imageUrl: data.content.imageUrl,
              imageSearchQuery: data.imageSearchQuery,
              culturalContext: data.content.culturalContext,
              materials: data.content.materials || [],
              instructions: data.content.instructions || [],
            });
            // Use stored unsplashPhoto if available
            if (data.unsplashPhoto) {
              setPhotoObject(data.unsplashPhoto);
            }
            // Store uploaded image info
            setActivity(prev => ({
              ...prev,
              unsplashPhoto: data.unsplashPhoto,
              uploadedImageUrl: data.uploadedImageUrl,
              imageCaption: data.imageCaption
            }));
            setIsFirestoreContent(true);
            setLoading(false);
            return;
          }
        }
        
        // Not found in Firestore, try static content
        const staticActivity = getContentById('activities', activityId);
        if (staticActivity) {
          setActivity(staticActivity);
          setIsFirestoreContent(false);
        }
        setLoading(false);
      },
      (error) => {
        // Error loading from Firestore, try static content
        const staticActivity = getContentById('activities', activityId);
        if (staticActivity) {
          setActivity(staticActivity);
          setIsFirestoreContent(false);
        }
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [activityId]);

  // Load photo object if not already set
  useEffect(() => {
    if (activity && !photoObject) {
      getCategoryPhotoObject('activities', activity, 0).then(photo => {
        if (photo) {
          setPhotoObject(photo);
        }
      });
    }
  }, [activity, photoObject]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg text-text-secondary">Loading activity...</div>
        </div>
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-white flex items-center justify-center">
        <div className="text-center">
          <div className="mb-4"><Icon name="🔍" size="4x" className="text-text-secondary" /></div>
          <div className="text-lg text-text-secondary">Activity not found</div>
          <button
            onClick={() => navigate('/explore')}
            className="mt-4 px-6 py-3 rounded-xl font-semibold bg-gradient-to-r from-primary to-primary-light text-white hover:-translate-y-0.5 hover:shadow-lg transition-all"
          >
            Back to Explore
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-14 pb-20 md:pt-0 md:pb-0">
      <PageHeader
        title={activity.title}
        onBack={() => navigate('/explore')}
        maxWidth="896px"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-8 md:py-10">

        <div className="bg-white rounded-card shadow-card overflow-hidden">
          {/* Hero Section */}
          <div className="w-full h-[200px] sm:h-[300px] bg-surface flex items-center justify-center rounded-t-card overflow-hidden relative">
            {activity.unsplashPhoto ? (
              <UnsplashImage
                photo={activity.unsplashPhoto}
                alt={activity.title}
                className="w-full h-full object-cover"
                triggerDownloadOnClick={false}
              />
            ) : activity.uploadedImageUrl ? (
              <div className="relative w-full h-full">
                <img 
                  src={activity.uploadedImageUrl} 
                  alt={activity.title}
                  className="w-full h-full object-cover"
                />
                {activity.imageCaption && (
                  <div className="absolute bottom-2 right-2 bg-black/40 backdrop-blur-sm px-2 py-1 rounded text-xs text-white">
                    {activity.imageCaption}
                  </div>
                )}
              </div>
            ) : photoObject ? (
              <UnsplashImage
                photo={photoObject}
                alt={activity.title}
                className="w-full h-full object-cover"
                triggerDownloadOnClick={false}
              />
            ) : activity.imageUrl ? (
              <img 
                src={activity.imageUrl} 
                alt={activity.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <Icon name={activity.image || '🎨'} size="5x" className="text-primary" />
            )}
          </div>

          <div className="p-6 sm:p-8 md:p-10">
            {/* Title and Meta */}
            <div className="mb-6">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                <div className="text-[11px] font-semibold text-text-secondary uppercase tracking-wide">
                  {activity.category}
                </div>
                <span className="text-text-secondary">•</span>
                <div className="text-[11px] font-semibold text-text-secondary">
                  Ages {activity.ageRange}
                </div>
                <span className="text-text-secondary">•</span>
                <div className="text-[11px] font-semibold text-text-secondary">
                  {activity.time}
                </div>
              </div>
              <h1 className="font-sans text-2xl sm:text-3xl md:text-4xl font-semibold text-text-primary mb-3 tracking-tight">
                {activity.title}
              </h1>
              <p className="text-base sm:text-lg text-text-secondary leading-relaxed">
                {activity.description}
              </p>
            </div>

            {/* Cultural Context */}
            {activity.culturalContext && (
              <div className="bg-surface rounded-xl p-5 sm:p-6 mb-6 sm:mb-8">
                <div className="text-sm font-semibold text-text-primary mb-2">Why This Matters</div>
                <p className="text-sm sm:text-base text-text-secondary leading-relaxed">
                  {activity.culturalContext}
                </p>
              </div>
            )}

            {/* Materials */}
            <div className="mb-6 sm:mb-8">
              <h2 className="font-sans text-xl sm:text-2xl md:text-3xl font-semibold text-text-primary mb-4 sm:mb-5 tracking-tight">
                Materials Needed
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {activity.materials.map((material, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 sm:p-4 bg-white border border-border rounded-lg"
                  >
                    <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-primary-light text-primary flex items-center justify-center text-xs font-semibold flex-shrink-0">
                      ✓
                    </div>
                    <div className="text-sm sm:text-base text-text-primary">
                      {material}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Instructions */}
            <div className="mb-6 sm:mb-8">
              <h2 className="font-sans text-xl sm:text-2xl md:text-3xl font-semibold text-text-primary mb-4 sm:mb-5 tracking-tight">
                Instructions
              </h2>
              <div className="space-y-4 sm:space-y-5">
                {activity.instructions.map((step, index) => (
                  <div
                    key={index}
                    className="flex gap-4 sm:gap-5 p-4 sm:p-5 bg-white border border-border rounded-lg"
                  >
                    <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary text-white flex items-center justify-center text-base sm:text-lg font-bold flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-1 text-sm sm:text-base text-text-primary leading-relaxed pt-1">
                      {step}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Clap Button */}
            <div className="flex justify-end mb-6">
              <ClapButton 
                contentId={activityId} 
                contentType="activity" 
                size="lg"
              />
            </div>
          </div>
        </div>

        {/* Comments Section */}
        <Comments contentId={activityId} contentType="activity" />
      </div>
    </div>
  );
}

